import React, { createContext, useContext, ReactNode } from "react";
import { useAuth } from "./AuthContext";

/**
 * User role types supported by the unified portal
 * - partner: Resellers managing multiple clients (net-30 billing available)
 * - client: Businesses managed by a partner (billing via partner)
 * - solo: Direct customers with no partner (prepaid only, no net-30)
 * - admin: AIIA staff
 */
export type UserRole = "partner" | "client" | "solo" | "admin";

/**
 * Navigation item for role-based menu rendering
 */
export interface RoleNavItem {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  roles: UserRole[];
}

/**
 * Context value providing role-based utilities
 */
interface RoleContextType {
  role: UserRole | null;
  isPartner: boolean;
  isClient: boolean;
  isSolo: boolean;
  isAdmin: boolean;
  hasAnyRole: (...roles: UserRole[]) => boolean;
  hasAllRoles: (...roles: UserRole[]) => boolean;
  getDefaultRoute: () => string;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

/**
 * RoleProvider - Provides role-based routing and access control utilities
 *
 * Uses the authenticated user's role from AuthContext to provide:
 * - Role checking utilities (isPartner, isClient, isSolo, isAdmin)
 * - Role-based routing helpers
 * - Default route resolution based on role
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isPartner, isClient } = useRole();
 *
 *   return (
 *     <div>
 *       {isPartner && <PartnerFeature />}
 *       {isClient && <ClientFeature />}
 *     </div>
 *   );
 * }
 * ```
 */
export function RoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const role = user?.role as UserRole | null;

  const isPartner = role === "partner";
  const isClient = role === "client";
  const isSolo = role === "solo";
  const isAdmin = role === "admin";

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (...roles: UserRole[]): boolean => {
    if (!role) return false;
    return roles.includes(role);
  };

  /**
   * Check if user has all of the specified roles (typically just one role at a time)
   */
  const hasAllRoles = (...roles: UserRole[]): boolean => {
    if (!role) return false;
    return roles.every((r) => r === role);
  };

  /**
   * Get default route based on user's role
   */
  const getDefaultRoute = (): string => {
    switch (role) {
      case "partner":
        return "/partner/dashboard";
      case "client":
        return "/client/dashboard";
      case "solo":
        return "/solo/dashboard";
      case "admin":
        return "/admin/dashboard";
      default:
        return "/";
    }
  };

  return (
    <RoleContext.Provider
      value={{
        role,
        isPartner,
        isClient,
        isSolo,
        isAdmin,
        hasAnyRole,
        hasAllRoles,
        getDefaultRoute,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

/**
 * useRole hook - Access role-based utilities
 *
 * Must be used within a RoleProvider
 *
 * @throws Error if used outside RoleProvider
 *
 * @example
 * ```tsx
 * function Navigation() {
 *   const { isPartner, isClient, isSolo, getDefaultRoute } = useRole();
 *
 *   return (
 *     <nav>
 *       {isPartner && <Link to="/partner/clients">My Clients</Link>}
 *       {isClient && <Link to="/client/instances">My Instances</Link>}
 *       {isSolo && <Link to="/solo/instances">My Instances</Link>}
 *     </nav>
 *   );
 * }
 * ```
 */
export function useRole(): RoleContextType {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
}

/**
 * RoleGuard - Conditionally render children based on user role
 *
 * @example
 * ```tsx
 * <RoleGuard roles={['partner', 'admin']}>
 *   <PartnerOnlyFeature />
 * </RoleGuard>
 * ```
 */
export function RoleGuard({
  roles,
  children,
  fallback = null,
}: {
  roles: UserRole[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { hasAnyRole } = useRole();

  if (!hasAnyRole(...roles)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
